import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  rescueDevicesQuery,
  type RescueDevicesData,
  type RescueDeviceItemGql,
  type RescueDeviceFilter,
} from '../lib/rescueQueries';

const PAGE_SIZE = 100;
const MAX_RETRIES = 3;

/**
 * Fetches every page of `rescueDevices` for the given filter and accumulates
 * them into a single array. Uses cache-first so repeat callers (e.g. Dashboard
 * and `useAdoptedPetIds`) share the same Apollo cache entry per filter.
 *
 * No mutation in the rescue portal directly forces a refetch of this query;
 * if one ever does, callers should add `rescueDevicesQuery` to their
 * `refetchQueries` so the accumulated pages stay in sync.
 */
export default function useAllDeviceItems(filter: RescueDeviceFilter): {
  items: RescueDeviceItemGql[];
  loading: boolean;
} {
  const { data, loading, fetchMore } = useQuery<RescueDevicesData>(rescueDevicesQuery, {
    variables: { input: { limit: PAGE_SIZE, offset: 0, filter } },
    fetchPolicy: 'cache-first',
  });

  const [extraItems, setExtraItems] = useState<RescueDeviceItemGql[]>([]);
  const paginating = useRef(false);
  const retries = useRef(0);
  const firstPageItems = data?.rescueDevices?.items;
  const totalCount = data?.rescueDevices?.totalCount ?? 0;
  // Memoize so the combined array has a stable reference across renders when
  // neither the first page nor the accumulated extras changed — otherwise
  // downstream memos (e.g. `useAdoptedPetIds`'s Set) recompute every render.
  const items = useMemo<RescueDeviceItemGql[]>(() => {
    const first = firstPageItems ?? [];
    return extraItems.length > 0 ? [...first, ...extraItems] : first;
  }, [firstPageItems, extraItems]);

  // Drive pagination off the authoritative `totalCount`, not the first page's
  // `hasMore`. `hasMore` is read only from the offset-0 result, which is fixed
  // under `cache-first`, so for any multi-page result it stays `true` forever —
  // wedging `loading` on and (via `useAdoptedPetIds`) hiding every rescue-row
  // action. Comparing accumulated length to `totalCount` flips to false exactly
  // when the last page is in hand.
  const hasMoreToFetch = items.length < totalCount;

  useEffect(() => {
    setExtraItems([]);
    retries.current = 0;
  }, [data]);

  useEffect(() => {
    if (!hasMoreToFetch || loading || paginating.current || retries.current >= MAX_RETRIES) {
      return;
    }
    let cancelled = false;
    paginating.current = true;
    fetchMore({
      variables: { input: { limit: PAGE_SIZE, offset: items.length, filter } },
    })
      .then(({ data: result }) => {
        if (!cancelled && result?.rescueDevices?.items?.length) {
          setExtraItems((prev) => [...prev, ...result.rescueDevices.items]);
          retries.current = 0;
        }
      })
      .catch(() => {
        if (!cancelled) {
          retries.current += 1;
        }
      })
      .finally(() => {
        paginating.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [hasMoreToFetch, loading, items.length, filter, fetchMore]);

  return { items, loading: loading || (hasMoreToFetch && retries.current < MAX_RETRIES) };
}
